/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import AntSelect from 'antd/lib/select';
import usePaginatedFetcher from 'hooks/usePaginatedFetcher';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const getNodeText = (node) => {
  if (['string', 'number'].includes(typeof node)) return node;
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (typeof node === 'object' && node) return getNodeText(node.props.children);
  return '';
};

export const filterOptionDefault = (input, option) => {
  const value = option.label?.$$typeof ? getNodeText(option?.label) : option.label;
  return value.toString()?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .indexOf(input.toString()?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()) >= 0;
};

const getTrueFalseOps = (ops = []) => [{ label: ops[0] || 'Sí', value: true }, { label: ops[1] || 'No', value: false }];
const activeOps = [{ label: 'Activo', value: true }, { label: 'Inactivo', value: false }];

export const defRender = (item, labelProp) => {
  const val = ['number', 'string'].includes(typeof item) ? item : item[labelProp];
  if (item?.tooltip) {
    return (
      <>
        {['number', 'string'].includes(typeof val) ? `${val} ` : val}
        <Tooltip title={item.tooltip}><QuestionCircleOutlined /></Tooltip>
      </>
    );
  }
  return val;
};

const Select = ({
  filterOption,
  render,
  dataSource: externalDataSource,
  keyProp,
  labelProp,
  disabled,
  valueProp,
  keyLabelRender,
  trueFalse,
  activeInactive,
  async: _async,
  loading: externalLoading,
  pagination,
  style,
  usingExternalDataSource,
  onClear,
  onChange,
  ...props
}) => {
  const [dataSource, fetchDataSource, loading] = usePaginatedFetcher(
    pagination?.getter,
    {
      ...pagination?.config,
      auto: usingExternalDataSource ? false : Boolean(pagination?.config?.auto),
    },
  );
  const q = useRef(null);
  const [options, setOptions] = useState([]);

  const keyLabelRenderDefault = (item) => (
    <>
      {item[keyProp]}
      {' - '}
      {item[labelProp]}
    </>
  );

  const keyLabelRenderFunc = (item) => (item.tooltip ? (
    <>
      {keyLabelRenderDefault(item)}
      {' '}
      <Tooltip title={item.tooltip}><QuestionCircleOutlined /></Tooltip>
    </>
  ) : keyLabelRenderDefault(item));

  const getLabel = (e) => {
    if (keyLabelRender) return keyLabelRenderFunc(e);
    if (render) return render(e);
    return defRender(e, labelProp);
  };

  const map = (e) => {
    if (e.children?.length || e.options?.length) {
      return {
        label: getLabel(e),
        value: e[valueProp] ?? e,
        disabled: !!e.disabled,
        options: (e.children || e.options).map(map),
      };
    }
    return {
      label: getLabel(e),
      value: e[valueProp] ?? e,
      disabled: !!e.disabled,
    };
  };

  useEffect(() => {
    const getOptionsAsync = async () => {
      const getLabelAsync = async (e) => {
        const val = await render(e);
        return val;
      };
      const asyncMap = async (e) => {
        const label = await getLabelAsync(e);
        return ({
          label,
          value: e[valueProp],
          disabled: !!e.disabled,
        });
      };
      const _options = _async
        ? await Promise.all([...externalDataSource.map(asyncMap)]) : externalDataSource.map(map);
      setOptions(_options);
    };

    if (!pagination || usingExternalDataSource) {
      if (trueFalse) {
        setOptions(getTrueFalseOps(trueFalse));
      } else if (activeInactive) {
        setOptions(activeOps);
      } else if (_async) {
        getOptionsAsync();
      } else {
        const mapped = (externalDataSource?.results || externalDataSource || [])
          .filter((e) => e).map(map);
        setOptions(mapped);
      }
    }
  }, [externalDataSource, usingExternalDataSource, valueProp]);

  useEffect(() => {
    if (pagination && !usingExternalDataSource) {
      if (pagination.config?.selfValue && !dataSource.results.length) {
        pagination.setter(externalDataSource);
        setOptions(externalDataSource.results.map(map));
      } else {
        pagination.setter(dataSource);
        setOptions(dataSource.results.map(map));
      }
    }
  }, [dataSource]);

  useEffect(() => {
    if (pagination && !usingExternalDataSource && !pagination.config?.selfValue) {
      fetchDataSource(1, q.current, pagination.params);
    }
  }, [pagination?.params]);

  const onPopupScroll = (e) => {
    if (props?.onPopupScroll) {
      props.onPopupScroll(e);
      return;
    }
    if (pagination && dataSource.next) {
      const { scrollHeight, scrollTop, clientHeight } = e.target;
      if ((scrollHeight - Math.ceil(scrollTop) === clientHeight
        && scrollTop !== 0) && dataSource?.next) {
        setTimeout(() => fetchDataSource(dataSource.page + 1,
          q.current, pagination.params));
      }
    }
  };

  const onInputKeyDown = (e) => {
    if (props?.onInputKeyDown) {
      props.onInputKeyDown(e);
      return;
    }
    if (e.key === 'Enter' && q.current !== e.target.value && pagination) {
      q.current = e.target.value;
      e.preventDefault();
      e.stopPropagation();
      fetchDataSource(1, q.current, pagination.params);
    }
  };

  if (disabled) {
    // eslint-disable-next-line no-param-reassign
    props.disabled = true;
  }

  return (
    <AntSelect
      showSearch
      allowClear={!trueFalse}
      showArrow={!disabled}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onInputKeyDown={onInputKeyDown}
      onPopupScroll={onPopupScroll}
      onChange={(val, opt) => {
        if (!val) {
          onClear();
        }
        onChange(val, opt);
      }}
      onClear={() => {
        q.current = null;
        onClear();
        if (pagination && usingExternalDataSource) {
          fetchDataSource(1, q.current, pagination.params);
        }
      }}
      optionFilterProp="children"
      filterOption={pagination ? false : ((o, i) => (filterOption || filterOptionDefault)(o, i))}
      notFoundContent={options.length ? 'No hay coincidencias' : (props.notFoundContent || 'No hay datos')}
      options={options}
      popupClassName="wrap-text"
      loading={externalLoading || (loading && !pagination?.config?.selfValue)}
      style={{ width: '100%', ...style }}
    />
  );
};

Select.propTypes = {
  filterOption: PropTypes.func,
  dataSource: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.shape()),
    PropTypes.shape({
      count: PropTypes.number,
      results: PropTypes.arrayOf(PropTypes.shape()),
    })]),
  render: PropTypes.func,
  valueProp: PropTypes.string,
  keyProp: PropTypes.string,
  disabled: PropTypes.bool,
  keyLabelRender: PropTypes.bool,
  labelProp: PropTypes.string,
  trueFalse: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.string)]),
  activeInactive: PropTypes.bool,
  onClear: PropTypes.func,
  async: PropTypes.bool,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    getter: PropTypes.func.isRequired,
    setter: PropTypes.func.isRequired,
    config: PropTypes.shape({
      auto: PropTypes.bool,
      selfValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    params: PropTypes.shape({}),
  }),
  style: PropTypes.shape({}),
  usingExternalDataSource: PropTypes.bool,
  onChange: PropTypes.func,
};

Select.defaultProps = {
  filterOption: null,
  dataSource: [],
  render: null,
  valueProp: 'id',
  keyProp: 'clave',
  disabled: false,
  keyLabelRender: false,
  labelProp: 'descripcion',
  trueFalse: false,
  activeInactive: false,
  async: false,
  loading: false,
  pagination: null,
  style: {},
  usingExternalDataSource: false,
  onChange: () => { },
  onClear: () => { },
};

export default Select;
